
import React, { useState } from 'react';
import NvFroala from 'froala/components/nv-froala';
import { FroalaViewMode, ToolbarOptions, UploadType } from 'froala/helpers/nv-froala-constants';
import { SanitizationLevel } from 'froala/helpers/sanitizer';
import NvTextArea from 'shared/components/inputs/nv-text-area';
import NvFileUpload, { NvFileUploadType } from 'shared/components/nv-file-upload';
import t from 'react-translate';
import { css } from '@emotion/react';
import { largeSpacing, standardSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';
import useUploadFile, { NovoEdFile } from 'shared/hooks/use-upload-file';
import NvFileUploadProgress from 'shared/components/nv-file-upload-progress';
import NvUploadedFile from 'shared/components/nv-uploaded-file';
import { S3NameSpaces } from 'shared/services/s3-upload-factory';
import { some } from 'underscore';
import { validateFiles } from 'froala/helpers/nv-froala-functions';
import { config as pendoConfig } from '../../../../../../config/pendo.config.json';
import { config } from '../../../../../../config/config.json';

/**
 * Generic component for rich text elements in Quizzes
 * @param { value, onchange, containerClass}
 * @returns A React component
 */
export const QuizRichTextElement = ({ value, onChange, containerClass, withCodeView = false, uploadType = UploadType.NONE, disabled = false }) => (
  <NvFroala
    className='option-content-froala'
    preset={FroalaViewMode.NORMAL}
    sanitizationLevel={SanitizationLevel.BASIC}
    toolbarButtons={[
      ToolbarOptions.COLOR,
      ToolbarOptions.BOLD,
      ToolbarOptions.ITALIC,
      ToolbarOptions.UNDERLINE,
      ToolbarOptions.FORMAT_UL,
      ToolbarOptions.FORMAT_OL,
      ToolbarOptions.PARAGRAPH_STYLE,
      ToolbarOptions.ALIGN,
      ToolbarOptions.INSERT_LINK,
    ]}
    uploadType={uploadType}
    withCodeView={withCodeView}
    minHeight={65}
    value={value}
    onChange={onChange}
    colorPickerContainer={containerClass}
    placeholder={disabled ? ' ' : ''}
    isDisabled={disabled}
  />
);

/**
 * Generic component for simple text elements in Quizzes
 * @param { value, onchange}
 * @returns A React component
 */
export const QuizSimpleTextComponent = ({ value, onChange, disabled = false }) => (
  <NvTextArea
    value={value}
    autoComplete='off'
    textareaClassName='text-large-body border-simple-text'
    onChange={(e) => onChange(e.currentTarget.value)}
    placeholder={disabled ? ' ' : ''}
    disabled={disabled}
  />
);

/**
 * Converts the index of a table into the right position depending on the columns
 * @param columns maximum columns
 * @param index the index in the list of elements in the table
 * @returns a String
 */
export const getTablePosition = (columns, index) => {
  if (columns <= 0 || index < 0) {
    return null;
  }

  // Calculate row and column position
  const row = Math.floor(index / columns) + 1;
  const col = index % columns;

  // Convert column number to letter(s) (like Excel: A, B, ..., Z, AA, AB, ...)
  function columnToLetter(colIndex) {
    let letters = '';
    let n = colIndex;
    while (n >= 0) {
      letters = String.fromCharCode((n % 26) + 65) + letters;
      n = Math.floor(n / 26) - 1;
    }
    return letters;
  }

  return columnToLetter(col) + row;
};

export const FileReplacement = ({ setTranslatedFile, fileType }) => {
  const { uploadFiles, isUploading, hasUploadPercentage, filesUploading, abortUpload } = useUploadFile();
  const [fileError, setFileError] = useState<string>('');
  const fileUpdloadStyles = css`
    width: 100%;
    justify-items: center;
    .nv-uploaded-file, .nv-file-upload-progress, .nv-file-upload-edit {
      width: 100%;
    }
    .nv-file-upload-progress {
      box-sizing: border-box;
    }
    .nv-file-upload-edit {
      .file-upload-container {
        padding: ${threeQuartersSpacing}px;
        .upload-file {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          height: ${largeSpacing}px;
          .icon-upload {
            font-size: 20px;
            padding-right: ${standardSpacing}px;
          }
        }
      }
    }
  `;
  const getUploadComponent = () => {
    const [document, setDocument] = useState<NovoEdFile>(null);

    const onUploadFile = async (files: File[], validate = false, fileIsRecording = false) => {
      let fError;
      if (validate) {
        fError = validateFiles(files, config.files.allUploadableExtensions);
      }

      if (fError) {
        setFileError(fError);
      } else {
        setFileError('');
        const [novoedFile] = await uploadFiles(files, S3NameSpaces.ATTACHMENTS);
        setDocument(novoedFile);
        setTranslatedFile({
          fileName: files[0].name,
          fileSize: files[0].size,
          fileType: files[0].type,
          uniqueId: novoedFile.uniqueId,
          novoedFile,
        });
      }
    };

    const onSelectError = (files) => {
      const hasFileError = some(files[0]?.errors, (error => error.code === 'file-too-large'));
      if (hasFileError) {
        setFileError(t.LECTURE_PAGES.COMPONENTS.VIDEO_PRACTICE.CREATE_PRACTICE_SCENARIO.SIZE_EXCEEDED());
      } else {
        setFileError('');
      }
    };

    const fileUploading = filesUploading[0];
    let content = (
      <NvFileUpload
        customText={t.FILE_UPLOAD.UPLOAD_FILE()}
        multiple={false}
        type={NvFileUploadType.ALL}
        onUploadFile={onUploadFile}
        maxSize='50MB'
        showactionButton={false}
        className='nv-file-upload-edit'
        onSelectError={onSelectError}
        dataQa={pendoConfig.pendo.settings.favicon.upload}
        noDrag={false}
      />
    );

    if (!fileError && isUploading) {
      content = (
        <NvFileUploadProgress
          hasUploadPercentage={hasUploadPercentage}
          uploadInProgress={isUploading}
          fileUploading={fileUploading}
          abortUpload={abortUpload}
          className='nv-file-upload-progress'
        />
      );
    }

    if (!fileError && document?.name) {
      content = (
        <NvUploadedFile
          file={document ?? fileUploading.novoEdFile}
          displayTopOptions={false}
          displayNameOnly
        />
      );
    }

    return content;
  };
  return (
    <div css={fileUpdloadStyles}>
      { getUploadComponent() }
    </div>
  );
};

export default getTablePosition;
